import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {assertAndContinue} from 'Asserts';
import BufferOptionsItem from 'buffers/BufferOptionsItem';
import Tooltip from 'ui-library/bs/Tooltip';
import {ButtonLink, ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

interface NoteProtectionIndicatorToggleProps {
  isEditable: boolean;
  isDisplayOnly: boolean;
  isProtected: boolean;
  isViewingOldVersion: boolean;
  isViewingPublishedVersion?: boolean;
  setProtected: (isProtected: boolean) => void;
}

function TemplateToggle(props: NoteProtectionIndicatorToggleProps): JSX.Element {
  function handleToggleSelection(modifiable: boolean): void {
    props.setProtected(!modifiable);
  }

  const tooltipText = props.isViewingOldVersion
    ? 'Editing disabled on previous versions'
    : props.isViewingPublishedVersion
      ? 'Editing disabled on published versions'
      : '';

  return (
    <BufferOptionsItem className={css(styles.tooltipWrapper)}>
      <div className="buttonGroup">
        <Tooltip title={tooltipText}>
          <ButtonLink
            className={css(styles.dropdownButton)}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            icon={!props.isProtected ? 'edit' : 'format-checkbox'}
            isDropdown={true}
            size={ButtonSize.MINI}
            text={!props.isProtected ? 'Modifiable' : 'Fill-in only'}
            disabled={props.isViewingOldVersion || props.isViewingPublishedVersion}
          />
        </Tooltip>
        <div className="dropdown-menu pull-right">
          <li>
            <a
              onClick={() => {
                handleToggleSelection(false);
              }}
            >
              <div className={css(styles.alignItemsBaseline)}>
                <BenchlingIcon icon="format-checkbox" />
                <div className={css(styles.flexBoxSpacing)}>
                  Fill-in only
                  <p className={css(styles.fontSizeSmall)}>
                    Entries created from this template will be locked. Users can only fill in tables, text
                    boxes, checkboxes, and files.
                  </p>
                </div>
              </div>
            </a>
          </li>
          <li>
            <a
              onClick={() => {
                handleToggleSelection(true);
              }}
            >
              <div className={css(styles.alignItemsBaseline)}>
                <BenchlingIcon icon="edit" />
                <div className={css(styles.flexBoxSpacing)}>
                  Modifiable
                  <p className={css(styles.fontSizeSmall)}>
                    Entries created from this template can be modified by end users.
                  </p>
                </div>
              </div>
            </a>
          </li>
        </div>
      </div>
    </BufferOptionsItem>
  );
}

function EntryTooltip(props: NoteProtectionIndicatorToggleProps): JSX.Element | null {
  if (props.isProtected) {
    const text = 'Fill-in only';
    const icon = 'format-checkbox';
    const title = 'You can only fill in tables, checkboxes and files.';

    return (
      <BufferOptionsItem className={css(styles.tooltipWrapper)}>
        <Tooltip title={title}>
          <div className={css(styles.alignItemsCenter)}>
            <BenchlingIcon icon={icon} />
            <div className={css(styles.isProtectedText)}>{text}</div>
          </div>
        </Tooltip>
      </BufferOptionsItem>
    );
  }

  return null;
}

function NoteProtectionIndicatorToggle(props: NoteProtectionIndicatorToggleProps): JSX.Element | null {
  if (props.isEditable) {
    return <TemplateToggle {...props} />;
  }
  if (props.isDisplayOnly) {
    return <EntryTooltip {...props} />;
  }
  assertAndContinue(
    !props.isViewingOldVersion,
    'Previous version should not be set for files that are not entries or templates'
  );
  return null;
}

export default NoteProtectionIndicatorToggle;

const styles = StyleSheet.create({
  tooltipWrapper: {
    height: '24px',
  },
  dropdownButton: {
    paddingTop: '6px',
    paddingRight: '4px',
    border: 'none',
    display: 'block',
  },
  fontSizeSmall: {
    fontSize: '11px',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    width: '260px',
  },
  alignItemsCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  alignItemsBaseline: {
    display: 'flex',
    alignItems: 'baseline',
  },
  flexBoxSpacing: {
    marginLeft: '11px',
    fontSize: '13px',
  },
  isProtectedText: {
    fontSize: '13px',
    marginLeft: '5px',
    marginRight: '5px',
  },
});
